"use client";

import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
type SectionLanguage = "en" | "it";
const transportOptions = {
  taxis: [{
    id: "uber-1",
    title: {
      en: "Uber",
      it: "Uber"
    },
    text: {
      en: "Available, but limited compared to U.S. cities and typically more expensive than taxis.",
      it: "Disponibile, ma più limitato rispetto alle città degli Stati Uniti e in genere più costoso dei taxi."
    }
  }, {
    id: "taxi-1",
    title: {
      en: "Taxis",
      it: "Taxi"
    },
    text: {
      en: "Easy to find throughout Rome. Use official white taxis or taxi stands; taxis can also be booked via the Uber app. Readily available from the Reception.",
      it: "Facili da trovare in tutta Roma. Consigliamo di usare i taxi bianchi ufficiali o i posteggi taxi; è anche possibile prenotarli tramite app."
    }
  }, {
    id: "priv-1",
    title: {
      en: "Private Driver",
      it: "NCC"
    },
    text: {
      en: "Giampiero Mazzotta, available for hire. For individual, group transfers, guided tours. Mobile: +39 392 1037240. Email: giampiero.mazzotta@gmail.com",
      it: "Giampiero Mazzotta, noleggio auto con conducente. Transfer singoli e di gruppo, visite guidate. Cell: +39 392 1037240. Email: giampiero.mazzotta@gmail.com"
    }
  }],
  other: [{
    id: "other-1",
    title: {
      en: "Metro & Transit",
      it: "Metro e Trasporti Pubblici"
    },
    text: {
      en: "Safe, affordable, and easy to use, though more limited than other major European cities. Walking plus taxis are often the simplest option.",
      it: "Sicuri, convenienti e facili da usare, anche se più limitati rispetto ad altre grandi città europee. Spostarsi a piedi e usare i taxi è spesso l’opzione più semplice."
    }
  }],
  rentalCars: [{
    id: "rental-1",
    title: {
      en: "Rental Cars",
      it: "Auto a Noleggio"
    },
    text: {
      en: "Driving in Rome can be quite challenging, and parking even more so. It took Michael three trips to Rome to even attempt to drive, and even then he was still relying on the expertise of Sara.",
      it: "Guidare a Roma può essere piuttosto impegnativo, e parcheggiare ancora di più. A Michael sono serviti tre viaggi a Roma prima ancora di provare a guidare, e anche allora contava ancora sull’esperienza di Sara."
    }
  }, {
    id: "rental-2",
    title: {
      en: "Our Take",
      it: "Il Nostro Parere"
    },
    text: {
      en: "For these reasons, it is hard for us to recommend renting a car. That said, it can be done if you are comfortable driving in busy cities, and it may be useful if you plan to travel outside the city.",
      it: "Per questi motivi, facciamo fatica a consigliare il noleggio di un’auto. Detto questo, può essere una buona soluzione se vi sentite a vostro agio a guidare in città trafficate e se pensate di spostarvi fuori Roma."
    }
  }, {
    id: "rental-3",
    title: {
      en: "If You Do Choose to Rent",
      it: "Se Scegliete di Noleggiare"
    },
    bullets: {
      en: ["We have had good experiences with Locauto and Sicily by Car.", "Try to look for companies located within Rome Fiumicino Airport.", "We also prefer to use Booking.com to compare and book rentals."],
      it: ["Abbiamo avuto buone esperienze con Locauto e Sicily by Car.", "Vi consigliamo di cercare compagnie situate all’interno dell’aeroporto di Roma Fiumicino.", "Noi preferiamo anche usare Booking.com per confrontare e prenotare le auto a noleggio."]
    }
  }]
};
function TransportCard({
  title,
  text,
  bullets
}: {
  title: string;
  text?: string;
  bullets?: string[];
}) {
  return <Card className="bg-card rounded-none p-6 pb-8 shadow-md border-border/40 hover:shadow-lg transition-all duration-300 flex flex-col">
      <div className="flex flex-col flex-grow px-2">
        <h3 className="font-serif text-2xl text-foreground leading-tight mb-4">
          {title}
        </h3>

        <div className="mt-auto bg-accent/30 p-5 border-l-2 border-primary/50 relative">
          <Sparkles className="size-4 text-primary absolute top-4 right-4 opacity-50" />

          {text && <p className="text-[15px] text-foreground/80 leading-7 pr-4">
              {text}
            </p>}

          {bullets && <ul className="text-[15px] text-foreground/80 leading-7 pr-4 space-y-3 list-disc pl-5">
              {bullets.map((bullet, index) => <li key={index}>{bullet}</li>)}
            </ul>}
        </div>
      </div>
    </Card>;
}
export default function RomanTransportation() {
  const [language, setLanguage] = useState<SectionLanguage>("en");
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  useEffect(() => {
    const handleStorageChange = () => {
      const savedLanguage = localStorage.getItem("site-language");
      if (savedLanguage === "en" || savedLanguage === "it") {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);
  const t = {
    en: {
      eyebrow: "Getting Around",
      title: "Transportation",
      intro: "Rome is best experienced on foot, with metro, ubers/taxis and short rides filling in the gaps.",
      taxisTab: "Ubers, Taxis, Private",
      otherTab: "Public Transit",
      rentalCarsTab: "Rental Cars"
    },
    it: {
      eyebrow: "Spostamenti",
      title: "Trasporti",
      intro: "Alcuni dei nostri consigli per chi viene daq fuori.",
      taxisTab: "Uber, Taxi, NCC",
      otherTab: "Trasporto Pubblico",
      rentalCarsTab: "Auto a Noleggio"
    }
  }[language];
  return <section id="roman-transportation" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-20">
          <h2 className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
            {t.eyebrow}
          </h2>

          <h1 className="font-serif text-5xl sm:text-6xl text-foreground leading-tight">
            {t.title}
          </h1>

          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            {t.intro}
          </p>
        </div>

        <Tabs defaultValue="taxis" className="w-full flex flex-col items-center">
          <TabsList className="mb-14 grid w-full grid-cols-1 gap-3 bg-transparent p-0 h-auto shadow-none sm:grid-cols-3 sm:max-w-2xl sm:gap-4">
            <TabsTrigger value="taxis" className="min-h-[52px] rounded-none border border-primary/30 bg-background px-4 py-3 text-[11px] tracking-[2px] uppercase text-foreground/80 transition-all duration-300 hover:border-primary/50 hover:shadow-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:px-6 sm:py-4 sm:text-[12px] sm:tracking-[3px]">
              {t.taxisTab}
            </TabsTrigger>

            <TabsTrigger value="other" className="min-h-[52px] rounded-none border border-primary/30 bg-background px-4 py-3 text-[11px] tracking-[2px] uppercase text-foreground/80 transition-all duration-300 hover:border-primary/50 hover:shadow-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:px-6 sm:py-4 sm:text-[12px] sm:tracking-[3px]">
              {t.otherTab}
            </TabsTrigger>

            <TabsTrigger value="rentalCars" className="min-h-[52px] rounded-none border border-primary/30 bg-background px-4 py-3 text-[11px] tracking-[2px] uppercase text-foreground/80 transition-all duration-300 hover:border-primary/50 hover:shadow-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md sm:px-6 sm:py-4 sm:text-[12px] sm:tracking-[3px]">
              {t.rentalCarsTab}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="taxis" className="w-full mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-12">
              {transportOptions.taxis.map(item => <TransportCard key={item.id} title={item.title[language]} text={item.text[language]} />)}
            </div>
          </TabsContent>

          <TabsContent value="other" className="w-full mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
              {transportOptions.other.map(item => <TransportCard key={item.id} title={item.title[language]} text={item.text[language]} />)}
            </div>
          </TabsContent>

          <TabsContent value="rentalCars" className="w-full mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
              {transportOptions.rentalCars.map(item => <TransportCard key={item.id} title={item.title[language]} text={item.text?.[language]} bullets={item.bullets?.[language]} />)}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}